import { api } from 'constants/API';
import { TestLocators } from 'constants/Locators';

import axios from 'axios';
import { CustomLink, MUI, ThemeMode } from 'ci-esi-web-shared';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { TrackVirtualPageView } from 'app/components/common/TrackVirtualPageView/TrackVirtualPageView';
import { LogoutRequestBody } from 'app/components/containers/layout/HeaderLandingPage/Profile/ProfileMenu/ProfileMenuBody';
import { actions as oktaActions } from 'app/components/pages/OktaLoginPage/slice';
import { selectErrorMessage } from 'app/containers/PrivateRoute/selectors';
import { actions } from 'app/containers/PrivateRoute/slice';
import { clearLastTokenRefreshDate } from 'helpers/cookies';
import { domainName } from 'helpers/domain';
import { colors } from 'styles/theme/colors';
import { SELECTED_DATABASE } from 'utils/cookie';

import { supportedErrorTypes } from './constants';

export function ForbiddenPage() {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const [description, setDescription] = useState(t(`FORBIDDEN.DESCRIPTION`));
  const dispatch = useDispatch();
  const errorMessage = useSelector(selectErrorMessage);
  const translationKey = Object.keys(supportedErrorTypes).find((key) => supportedErrorTypes[key] === errorMessage);

  const logoutRequestData: LogoutRequestBody = {
    logoutPageUrl: window.location.toString(),
    source: 'Logout Button',
    domain: domainName(),
  };

  const handleClose = () => {
    dispatch(oktaActions.clearUserInfoFromOkta());
    clearLastTokenRefreshDate();
    document.cookie = `${SELECTED_DATABASE}= ; Domain=${domainName()}; path=/;`;
    axios.delete(api.logout, { data: logoutRequestData }).finally(() => {
      window.location.href = `${process.env.REACT_APP_IHS_AUTH_URL}/clearsession`;
    });
  };

  useEffect(() => {
    dispatch(actions.clearUserInfo());
  }, [dispatch]);

  useEffect(() => {
    if (translationKey) {
      setDescription(t(`FORBIDDEN.${translationKey}`));
    }
  }, [dispatch, t, translationKey]);

  const wrapperSx = {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute' as const,
    left: 0,
    right: 0,
  };
  const cardSx = {
    minWidth: '800px',
    background: isDarkMode ? colors.grey60 : colors.primaryWhite,
    opacity: isDarkMode ? 0.85 : 1,
    padding: 3,
    boxShadow: '0px 16px 16px 0 #00000024',
    borderRadius: '1px',
  };
  const textSx = { color: isDarkMode ? colors.primaryWhite : colors.grey40 };
  const titleSx = {
    fontWeight: (theme.typography.fontWeightRegular ?? 400) as number,
    fontSize: '24px',
    textAlign: 'center' as const,
    marginBottom: '20px',
  };
  const infoWrapperSx = { marginTop: '24px' };
  const infoSx = {
    fontSize: '16px',
    fontWeight: (theme.typography.fontWeightRegular ?? 400) as number,
  };
  const linkSx = { textTransform: 'none' as const };
  const dividerSx = {
    background: isDarkMode ? colors.grey44 : colors.grey93,
    marginLeft: -3,
    marginRight: -3,
  };
  const buttonContainerSx = { display: 'flex', justifyContent: 'flex-end' };
  const buttonSx = {
    color: isDarkMode ? colors.primaryWhite : colors.grey40,
    position: 'relative' as const,
    padding: '10px 24px',
    fontSize: '16px',
    '&:hover': {
      backgroundColor: isDarkMode ? colors.white14 : colors.grey93,
    },
  };

  return (
    <>
      <Helmet>
        <title>{t(`FORBIDDEN.META_TITLE`)}</title>
      </Helmet>
      <TrackVirtualPageView pageTitle={t(`FORBIDDEN.META_TITLE`)} />
      <MUI.Box sx={wrapperSx}>
        <MUI.Box sx={cardSx}>
          <MUI.Box sx={textSx}>
            <MUI.Typography sx={titleSx}>{t(`FORBIDDEN.ERROR`)}</MUI.Typography>
            <MUI.Divider sx={dividerSx} />
            <MUI.Box sx={infoWrapperSx}>
              <MUI.Typography component="span" sx={infoSx} data-testid={TestLocators.NoAccess_Msge}>
                {description}
              </MUI.Typography>
            </MUI.Box>
            <MUI.Box sx={infoWrapperSx}>
              <MUI.Typography component="span" sx={infoSx} data-testid={TestLocators.NoAccess_Contact_Msge}>
                {t(`FORBIDDEN.SUB_DESCRIPTION.PHONE_TEXT`)}
              </MUI.Typography>
              <CustomLink
                href={`tel:${t(`FORBIDDEN.SUB_DESCRIPTION.PHONE_LINK`)}`}
                text={t(`FORBIDDEN.SUB_DESCRIPTION.PHONE_LINK`)}
                data-testid={TestLocators.NoAccess_Phone}
                sx={{ ...infoSx, ...linkSx }}
              />
              <MUI.Typography component="span" sx={infoSx} data-testid={TestLocators.NoAccess_Or_Msge}>
                {t(`FORBIDDEN.SUB_DESCRIPTION.EMAIL_TEXT`)}
              </MUI.Typography>
              <CustomLink
                href={`mailto:${t(`FORBIDDEN.SUB_DESCRIPTION.EMAIL_LINK`)}`}
                text={t(`FORBIDDEN.SUB_DESCRIPTION.EMAIL_LINK`)}
                data-testid={TestLocators.NoAccess_Email}
                sx={{ ...infoSx, ...linkSx }}
              />
            </MUI.Box>
          </MUI.Box>
          <MUI.Box sx={{ ...infoWrapperSx, ...buttonContainerSx }} onClick={handleClose}>
            <MUI.Button sx={buttonSx}>{t('COMMON.CLOSE')}</MUI.Button>
          </MUI.Box>
        </MUI.Box>
      </MUI.Box>
    </>
  );
}
